import "./carousel.css";

import Image from "next/image";
import Link from "next/link";
import React from "react";
import BootstrapCarousel from "react-bootstrap/Carousel";

import { MerchandisingItem } from "@/graphql/types/graphql";
import { Search_Type } from "@/graphql/types/graphql";
import { encodeURL } from "@/utils/index";

interface WebCarouselProps {
  items: MerchandisingItem[];
  numberPerSlide?: number;
  handleItemClick: (item: MerchandisingItem) => void;
  isLoop?: boolean;
  isEdits?: boolean;
  searchType?: Search_Type;
}

const WebCarousel: React.FC<WebCarouselProps> = ({
  items = [],
  numberPerSlide = 4,
  handleItemClick,
  isLoop = true,
  isEdits = true,
  searchType,
}) => {
  const [activeIndex, setActiveIndex] = React.useState(!isLoop ? 0 : undefined);

  const slides = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < items.length; i += numberPerSlide) {
      let chunk = items.slice(i, i + numberPerSlide);

      if (chunk.length < numberPerSlide && result.length > 0) {
        const prevChunk = result[result.length - 1];
        const missingCount = numberPerSlide - chunk.length;
        chunk = prevChunk.slice(-missingCount).concat(chunk);
      }

      result.push(chunk);
    }
    return result;
  }, [items, numberPerSlide]);

  const isSearch = numberPerSlide === 4;

  const itemTitleStyle = isSearch
    ? {
        marginTop: 24,
        fontWeight: 500,
      }
    : {
        marginTop: 16,
        fontWeight: 700,
      };

  const carouselClassName = React.useMemo(() => {
    if (!isLoop) {
      if (activeIndex === 0) {
        return "no-left";
      } else if (activeIndex === slides.length - 1) {
        return "no-right";
      } else {
        return "";
      }
    }
    return "";
  }, [isLoop, activeIndex, slides]);

  const handleSlideChange = (selectedIndex: number) => {
    if (!isLoop) {
      setActiveIndex(selectedIndex);
    }
  };

  if (items.length === 0) {
    return <></>;
  }

  return (
    <>
      <BootstrapCarousel
        interval={null}
        indicators={false}
        activeIndex={activeIndex}
        onSelect={handleSlideChange}
        className={carouselClassName}
        prevIcon={
          !isLoop && activeIndex === 0 ? (
            <></>
          ) : (
            <div className="carousel-arrow">
              <img src="/back.svg" alt="previous" />
            </div>
          )
        }
        nextIcon={
          !isLoop && activeIndex === slides.length - 1 ? (
            <></>
          ) : (
            <div className="carousel-arrow right-arrow">
              <img src="/back.svg" alt="next" />
            </div>
          )
        }
      >
        {slides.map((slide, index) => {
          return (
            <BootstrapCarousel.Item key={index}>
              <div className="web-carousel-item">
                {slide.map((item, index) => {
                  return (
                    <div
                      key={item.text}
                      className="web-carousel-illustration"
                      style={{
                        width: `${100 / numberPerSlide - 2}%`,
                        marginLeft:
                          index !== 0
                            ? `${(2 * numberPerSlide) / (numberPerSlide - 1)}%`
                            : 0,
                      }}
                    >
                      <Link
                        href={
                          !item.brandID
                            ? `/${isEdits ? "edits" : "results"}/${encodeURL(
                                item.searchQuery || ""
                              )}${
                                searchType === Search_Type.Item
                                  ? `?type=${searchType}`
                                  : ""
                              }`
                            : `/brand/${encodeURL(item.text)}/${item.brandID}`
                        }
                        onClick={() => handleItemClick(item)}
                      >
                        <Image
                          src={item.image}
                          alt={item.text}
                          loading="lazy"
                          width={312.5}
                          height={500}
                          style={{
                            flex: "auto",
                            objectFit: "cover",
                            objectPosition: "center bottom",
                            aspectRatio: 0.625,
                            width: "-webkit-fill-available",
                          }}
                        />
                        <div
                          className="illustration-desc"
                          style={itemTitleStyle}
                        >
                          {item.text}
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </BootstrapCarousel.Item>
          );
        })}
      </BootstrapCarousel>
      <style jsx>{`
        .web-carousel-item {
          display: flex;
          align-items: stretch;

          .web-carousel-illustration {
            display: flex;
            flex-direction: column;

            .illustration-desc {
              color: #191919;
              font-size: 16px;
              font-style: normal;
              font-weight: 500;
              line-height: normal;
              letter-spacing: -0.32px;
              margin-top: 24px;
              text-align: left;
              height: 57px;
              overflow: hidden;
              text-overflow: ellipsis;
              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              font-smooth: never;
            }
          }
        }

        .carousel-arrow {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e7e7e7;
          background: #fff;
          & > img {
            width: 16px;
            height: 16px;
          }
          &.right-arrow > img {
            transform: rotate(180deg);
          }
        }
      `}</style>
    </>
  );
};

export default WebCarousel;
